import { cx } from '@linaria/core';
import { useRef, useLayoutEffect } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { breakpoints } from '@ui/css';
import { useComponentInteractions } from '@utils/component-framework';
import { useMatchBreakpoint, useFullscreenState, useChangeEffect, ShowFor, SizeTransition } from '@utils/react';
import '@i-ark/models';
import { useUpdateEntryLocationMutation } from '../state/mutations/entries/location.update.js';
import '@tanstack/react-query';
import { useCurrentPage } from '../state/hook.current-page.js';
import { iArkStore } from '../state/store.js';
import { fullscreenButton, navButton, navButtonTrack, wrapper, transition, transitionDurationMs, section, contentWrapper } from './_content.style.js';
import { NavigationButton } from './atom.navigation-button.js';
import { usePreloadImages } from './hook.preload-images.js';
import { useShortcut } from './hook.use-shortcut.js';
import { SheetWrapper } from './organism.sheet/_sheet.js';
import { sheetTokens, sheetAspectRatio } from './organism.sheet/_sheet.style.js';
import { useIArkFullscreenActions } from './organism.tools/hooks/useFullscreenActions.js';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';

const IArkContent = function App(props) {
  const internalRef = useRef(null);
  const ref = props.ref ?? internalRef;
  const internalContentWrapperRef = useRef(null);
  const contentWrapperRef = props.contentWrapperRef ?? internalContentWrapperRef;
  const isMobile = useMatchBreakpoint(breakpoints.mobile);
  const interactions = useComponentInteractions();
  const entryKey = iArkStore.useSelector(state => state.entryKey);
  const dispatch = iArkStore.useDispatch();
  const updateLocation = useUpdateEntryLocationMutation(entryKey);
  const {
    page,
    index
  } = useCurrentPage(props.node.component.data, entryKey);
  const {
    fullscreenEnabled
  } = useFullscreenState();
  const isFullscreen = fullscreenEnabled || props.context === "lightbox";
  const {
    overlayId,
    rootSelector,
    ...componentProps
  } = props;
  const {
    handleFullscreenClick
  } = useIArkFullscreenActions({
    overlayId,
    rootSelector,
    componentProps
  });
  const zoom = iArkStore.useSelector(state => state.zoom);
  const prevIndex = useRef(index);
  useLayoutEffect(() => {
    return () => {
      prevIndex.current = index;
    };
  }, [index]);
  useChangeEffect(() => {
    if (page?.id) {
      updateLocation({
        pageId: page.id
      });
    }
  }, [page?.id]);
  const handleDeselect = () => {
    dispatch(iArkStore.actions.deselectItem());
  };
  useShortcut("Escape", handleDeselect);
  usePreloadImages(index);
  if (!page) {
    return null;
  }
  const transitionDirection = index > prevIndex.current ? "forwards" : "backwards";
  return /*#__PURE__*/jsxs(Fragment, {
    children: [!isFullscreen && /*#__PURE__*/jsx(ShowFor, {
      breakpoint: breakpoints.mobile,
      children: /*#__PURE__*/jsx("button", {
        "aria-label": "Aktiv\xE9r komponent",
        className: fullscreenButton,
        onClick: handleFullscreenClick
      })
    }), /*#__PURE__*/jsxs("div", {
      ref: ref,
      className: cx(section, transitionDirection),
      onMouseDown: handleDeselect,
      onTouchStart: handleDeselect,
      onClick: interactions.onActivate,
      style: sheetTokens.zoom.__override(zoom),
      "data-context": props.context,
      "data-fullscreen": isFullscreen,
      "aria-hidden": isMobile && !isFullscreen,
      children: [!isFullscreen && /*#__PURE__*/jsx("span", {
        className: cx(navButtonTrack, "left"),
        children: /*#__PURE__*/jsx("span", {
          className: cx(navButton, "left"),
          children: /*#__PURE__*/jsx(NavigationButton, {
            direction: "backwards",
            component: props.node.component
          })
        })
      }), /*#__PURE__*/jsx("div", {
        className: wrapper,
        ref: contentWrapperRef,
        tabIndex: -1,
        children: /*#__PURE__*/jsxs(TransitionGroup, {
          component: null,
          children: [!isFullscreen && /*#__PURE__*/jsx(SizeTransition, {
            axis: "vertical",
            swappable: true,
            retainClassnamesOnExit: true,
            timeout: transitionDurationMs,
            classNames: transition,
            delayMs: {
              enter: 10,
              exit: 10
            },
            children: ref => /*#__PURE__*/jsx(TransitionContent, {
              ...props,
              ref: ref,
              index: index,
              page: page
            })
          }, index), isFullscreen && /*#__PURE__*/jsx(SizeTransition, {
            axis: "both",
            swappable: true,
            retainClassnamesOnExit: true,
            timeout: transitionDurationMs,
            classNames: transition,
            delayMs: {
              enter: 10,
              exit: 10
            },
            children: ref => /*#__PURE__*/jsx(TransitionContent, {
              ...props,
              ref: ref,
              index: index,
              page: page
            })
          }, index)]
        })
      }), !isFullscreen && /*#__PURE__*/jsx("span", {
        className: cx(navButtonTrack, "right"),
        children: /*#__PURE__*/jsx("span", {
          className: cx(navButton, "right"),
          children: /*#__PURE__*/jsx(NavigationButton, {
            direction: "forwards",
            component: props.node.component,
            onClick: () => {
              contentWrapperRef.current?.focus({
                preventScroll: true
              });
            }
          })
        })
      })]
    })]
  });
};
function TransitionContent({
  index,
  page,
  ...props
}) {
  return /*#__PURE__*/jsx("div", {
    ref: props.ref,
    className: transition,
    style: sheetAspectRatio.__override(page.aspectRatio),
    children: /*#__PURE__*/jsx("div", {
      className: contentWrapper,
      children: /*#__PURE__*/jsx(SheetWrapper, {
        defaultInputColor: props.node.component.data.style.colors.inputTextColor ?? "#000",
        defaultTextColor: props.node.component.data.style.colors.textColor ?? "#000",
        page: page,
        context: props.context
      }, index)
    })
  });
}

export { IArkContent };
//# sourceMappingURL=_content.js.map
